import { domainMaxBounds } from '../domain/limits'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import maplibregl from 'maplibre-gl'
import { useQuery } from '@tanstack/react-query'
import { cabinetApi } from '../api'
import { baseStyle } from '../map/baseStyle'
import { registerBasemapMap } from '../map/basemapRefresh'
import { createStyleGate } from '../map/styleGate'
import { BoundaryToggles } from '../map/BoundaryToggles'
import { MapPanel } from '../map/MapPanel'
import { parseBoundaryUrl, writeBoundaryUrl } from '../map/boundaryUrl'
import { boxKey, boundsToBox, useBoundaryOverlays } from '../map/useBoundaryOverlays'
import type { BBox } from '../api'
import { useMapView } from '../map/mapView'
import { CollapsibleLegend } from '../map/CollapsibleLegend'
import { useT } from '../i18n'
import {
  CATEGORY_META,
  SAT_ICON_PREFIX,
  satIconExpr,
  categoryLabel,
  categoryLegendItems,
  categoryColor,
} from './satLayer'
import {
  EMPTY_FILTER,
  buildFilterExpression,
  buildRegistryIndex,
  positionsToFilterableGeoJSON,
  toggleFilterValue,
  visibleCount,
  type FilterState,
} from './satFilter'
import { FilterPanel } from './FilterPanel'
import { stepTime, toApiTime } from './timeControls'
import { parseSatUrl, writeSatUrl } from './satUrl'
import { orbitTrackToGeoJSON } from './orbitTrack'
import { coverageFC } from './coverage'
import { TimeToolbar } from './TimeToolbar'
import { SatelliteCard } from './SatelliteCard'
import { PassesPanel } from './PassesPanel'
import { BasemapAttribution } from '../map/attribution/BasemapAttribution'

// Мировой вид по умолчанию (весь земной шар), пока URL не задал свой.
const WORLD_CENTER: [number, number] = [0, 20]
const WORLD_ZOOM = 1.4

// Подписи категорий реестра (см. фазы 1–2 бэкенда) берём из общего satLayer,
// чтобы сводка реестра и легенда карты не расходились ни цветом, ни словом.

const SAT_SOURCE_ID = 'sat-positions'
const SAT_LAYER_ID = 'sat-positions-layer'

// Слой орбиты выбранного спутника: тонкая линия витка + маркер текущей позиции.
// Оба живут ПОД маркерами позиций (beforeId = SAT_LAYER_ID), чтобы линия не
// перекрывала кликабельные пиктограммы. Цвет — категории спутника (setPaintProperty).
const ORBIT_SOURCE_ID = 'sat-orbit'
const ORBIT_LAYER_ID = 'sat-orbit-line'
const ORBIT_DOT_SOURCE_ID = 'sat-orbit-current'
const ORBIT_DOT_LAYER_ID = 'sat-orbit-current-dot'

// Кольца охвата выбранного спутника: внешний след приёма (footprint, сплошная) и
// внутренняя мёртвая зона (dead-zone, пунктир). Один GeoJSON-источник, два line-
// слоя: MapLibre НЕ поддерживает data-driven line-dasharray (свойство cross-faded,
// только по зуму), поэтому «пунктир против сплошной» = два слоя, разведённых по
// свойству ring фильтром. Оба живут ПОД маркерами (beforeId = SAT_LAYER_ID) и ниже
// линии орбиты; цвет — категории спутника (setPaintProperty).
const COVERAGE_SOURCE_ID = 'sat-coverage'
const COVERAGE_LINE_ID = 'sat-coverage-line'
const COVERAGE_DEAD_ID = 'sat-coverage-dead-line'

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] }

/** Категория из «сырой» карточки орбиты; не строка → unknown. */
function orbitCategory(card: Record<string, unknown>): string {
  return typeof card.category === 'string' && card.category ? card.category : 'unknown'
}

// Логический размер пиктограммы (px). Отдаём образ в devicePixelRatio× и с тем же
// pixelRatio — тогда на экране пиктограмма занимает ровно SAT_ICON_PX независимо
// от плотности дисплея.
const SAT_ICON_PX = 22

/** Рисует ОДНУ пиктограмму спутника заданного цвета → ImageData.
 *
 *  v1 «свой значок» = общий силуэт (корпус + две панели + антенна), окрашенный в
 *  цвет категории. Бэспоук-силуэта на категорию пока нет — различает именно цвет.
 *  Силуэт лежит на полупрозрачном светлом диске с тонким цветным кольцом, чтобы
 *  читаться на светлой carto-подложке. */
function drawSatIcon(color: string, ratio: number): ImageData | null {
  const s = SAT_ICON_PX * ratio
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')
  if (!ctx) return null
  const mid = s / 2

  // Подложка-диск: полупрозрачная белая + тонкое кольцо цвета категории.
  ctx.beginPath()
  ctx.arc(mid, mid, s * 0.46, 0, Math.PI * 2)
  ctx.fillStyle = 'rgba(255,255,255,0.86)'
  ctx.fill()
  ctx.lineWidth = 1.3 * ratio
  ctx.strokeStyle = color
  ctx.stroke()

  ctx.fillStyle = color
  ctx.strokeStyle = color
  ctx.lineJoin = 'round'
  ctx.lineCap = 'round'

  // Корпус спутника — по центру.
  const bodyW = s * 0.16
  const bodyH = s * 0.26
  ctx.fillRect(mid - bodyW / 2, mid - bodyH / 2, bodyW, bodyH)

  // Две панели солнечных батарей по бокам корпуса.
  const panelW = s * 0.2
  const panelH = s * 0.15
  const gap = s * 0.035
  const panelY = mid - panelH / 2
  ctx.fillRect(mid - bodyW / 2 - gap - panelW, panelY, panelW, panelH)
  ctx.fillRect(mid + bodyW / 2 + gap, panelY, panelW, panelH)
  // Продольные жилки панелей — светлым, чтобы читались как «батареи», а не бруски.
  ctx.strokeStyle = 'rgba(255,255,255,0.85)'
  ctx.lineWidth = 0.7 * ratio
  ctx.beginPath()
  ctx.moveTo(mid - bodyW / 2 - gap - panelW / 2, panelY)
  ctx.lineTo(mid - bodyW / 2 - gap - panelW / 2, panelY + panelH)
  ctx.moveTo(mid + bodyW / 2 + gap + panelW / 2, panelY)
  ctx.lineTo(mid + bodyW / 2 + gap + panelW / 2, panelY + panelH)
  ctx.stroke()

  // Штанга-антенна вверх от корпуса.
  ctx.strokeStyle = color
  ctx.lineWidth = 1.4 * ratio
  ctx.beginPath()
  ctx.moveTo(mid, mid - bodyH / 2)
  ctx.lineTo(mid, mid - bodyH / 2 - s * 0.12)
  ctx.stroke()

  return ctx.getImageData(0, 0, s, s)
}

/** Регистрирует по одной пиктограмме на категорию (idempotent, кэш по hasImage). */
function ensureSatIcons(map: maplibregl.Map) {
  const ratio = Math.max(1, Math.round(window.devicePixelRatio || 1))
  for (const cat of Object.keys(CATEGORY_META)) {
    const id = SAT_ICON_PREFIX + cat
    if (map.hasImage(id)) continue
    const img = drawSatIcon(CATEGORY_META[cat].color, ratio)
    if (img) map.addImage(id, img, { pixelRatio: ratio })
  }
}

export function SatellitesPage() {
  const t = useT()
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  // Шлюз «стиль разобран». Все записи в источники/слои идут через него: прямая
  // проверка isStyleLoaded() роняет обновление, пока грузятся тайлы (carto после
  // пана, GeoJSON сразу после своего setData) — апдейт молча терялся без ретрая
  // (орбита застревала после закрытия карточки, позиции замерзали до 60 с). См.
  // map/styleGate.ts и MapCanvas.tsx.
  const gateRef = useRef(createStyleGate())

  // Состояние раздела (время/фильтр/выбор) синхронизируется с URL. Снимок URL на
  // момент монтирования читаем один раз (сид из ссылки: рефреш/шаринг), дальше
  // ведёт effect-писатель ниже. useMapView владеет lng/lat/zoom через свой
  // экземпляр useSearchParams — наш писатель трогает только 6 своих ключей через
  // функциональный updater, поэтому два писателя не затирают друг друга.
  const [searchParams, setSearchParams] = useSearchParams()
  const bootRef = useRef<ReturnType<typeof parseSatUrl>>()
  if (!bootRef.current) bootRef.current = parseSatUrl(searchParams)
  const boot = bootRef.current

  // Вид карты (центр/зум) синхронизируется с URL — как в «Картах»/«Моделях».
  const { center, zoom, onMoveEnd } = useMapView()
  // moveend читается из свежего колбэка через ref, чтобы карту создавать один раз.
  const moveEndRef = useRef(onMoveEnd)
  moveEndRef.current = onMoveEnd
  // Начальный вид фиксируем на монтировании (из URL или мировой), чтобы записи
  // вида в URL не пересоздавали карту.
  const initialViewRef = useRef({ center: center ?? WORLD_CENTER, zoom: zoom ?? WORLD_ZOOM })

  // Выбранный момент времени: null — живой режим «сейчас» (автообновление), Date —
  // зафиксированный инстант (пропагация TLE на него, без автопрокрутки вперёд).
  const [selectedTime, setSelectedTime] = useState<Date | null>(boot.selectedTime)
  // NORAD выбранного спутника (клик по маркеру) — для орбиты и карточки.
  const [selectedNorad, setSelectedNorad] = useState<string | null>(boot.selectedNorad)
  // Состояние фасетного фильтра карты (тип/компания/орбита/юрлицо/статус).
  const [filter, setFilter] = useState<FilterState>(boot.filter)
  // Показывать кольца охвата (footprint + мёртвая зона) выбранного спутника.
  // Локальное состояние карты — в URL не выносим (в отличие от выбора/времени/фильтра).
  const [showCoverage, setShowCoverage] = useState(false)
  // Рубежи — границы ИЭЗ, промысловые и запретные районы (просьба владельца
  // 19.09.2026: «доступны на всех картах»). Охват спутника читают против
  // промысловых районов, а не против пустого моря.
  const [boundaries, setBoundaries] = useState(() => parseBoundaryUrl(searchParams))
  const [box, setBox] = useState<BBox | null>(null)
  // Свежий фильтр в ref — чтобы применять его после каждой смены данных источника,
  // не пересобирая GeoJSON при изменении самого фильтра (см. applyFilter/positions).
  const filterRef = useRef(filter)
  filterRef.current = filter

  // Единственный писатель раздела в URL. writeSatUrl копирует prev и трогает ТОЛЬКО
  // свои 6 ключей (sat/t/cat/co/orb/leg/st), сохраняя lng/lat/zoom (ими владеет
  // useMapView). Функциональный updater берёт живой снимок react-router, поэтому
  // запись вида карты в том же цикле не теряется. Зависимости — ровно три состояния;
  // при равном URL setSearchParams не создаёт новой записи истории (replace) и не
  // зацикливается (значения состояний не меняются в ответ на запись).
  useEffect(() => {
    setSearchParams(
      prev => writeBoundaryUrl(writeSatUrl(prev, { selectedNorad, selectedTime, filter }), boundaries),
      { replace: true },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNorad, selectedTime, filter, boundaries])

  // Под символы спутников: линия поверх маркера перечёркивала бы его.
  const boundaryMeta = useBoundaryOverlays({
    mapRef, gateRef, on: boundaries, viewKey: boxKey(box), beforeId: SAT_LAYER_ID,
  })

  const apiTime = selectedTime ? toApiTime(selectedTime) : undefined

  // Живой режим (момент не выбран) — автообновление раз в минуту; зафиксированный
  // инстант не должен сам уезжать вперёд. Общий период для позиций И орбиты, иначе
  // текущая точка/значения выбранного спутника замерзали бы, пока его маркер идёт.
  const liveRefetch = selectedTime ? false : 60_000

  const { data, isLoading, isError } = useQuery({
    queryKey: ['cabinet', 'satellites', 'registry'],
    queryFn: () => cabinetApi.satellites.registry(),
  })

  // Мгновенные позиции (пропагация TLE на бэкенде). В живом режиме обновляются раз
  // в минуту — спутники идут по орбите, статичная карта устарела бы за минуты. При
  // выбранном моменте автообновление отключаем: зафиксированный инстант не должен
  // сам уезжать вперёд. Ключ включает момент — смена времени = новая выборка.
  const positionsQuery = useQuery({
    queryKey: ['cabinet', 'satellites', 'positions', apiTime ?? 'now'],
    queryFn: () => cabinetApi.satellites.positions(apiTime),
    refetchInterval: liveRefetch,
  })
  const positions = positionsQuery.data

  // Орбита + виток выбранного спутника на тот же момент. Ключ включает момент —
  // сдвиг времени при выбранном спутнике перезапрашивает его орбиту. В живом режиме
  // обновляется в том же темпе, что и позиции: иначе текущая точка/паспорт спутника
  // замерзали бы на миг клика, пока маркер продолжает идти.
  const orbitQuery = useQuery({
    queryKey: ['cabinet', 'satellites', 'orbit', selectedNorad, apiTime ?? 'now'],
    queryFn: () => cabinetApi.satellites.orbit(selectedNorad!, apiTime),
    enabled: selectedNorad !== null,
    refetchInterval: liveRefetch,
  })
  // Данные react-query переживают disable — держим орбиту только пока выбор активен,
  // иначе после закрытия карточки линия осталась бы висеть на карте.
  const orbit = selectedNorad ? orbitQuery.data : undefined

  // GeoJSON позиций держим в ref: слой создаётся на 'load' карты, а данные могут
  // прийти раньше или позже — на 'load' берём последнее из ref, а свежие данные
  // доезжают через getSource(...).setData ниже.
  const geojsonRef = useRef<GeoJSON.FeatureCollection>({ type: 'FeatureCollection', features: [] })

  // Индекс реестра norad_id → запись: позиции несут category/orbit_class/status/
  // operator, но НЕ company_key/legal_entity — обогащаем ими фичи из реестра, чтобы
  // фильтр по компании/юрлицу работал через дешёвый setFilter (без setData).
  const registryById = useMemo(() => buildRegistryIndex(data?.satellites ?? []), [data])

  // Поток позиций созвездия выбранного аппарата. Ключ — company_key из реестра
  // (в позициях его нет). Нет company_key либо созвездия нет среди поставщиков —
  // null, и карточка честно скажет «нет атрибуции», а не «данные не идут».
  const selectedFeed = useMemo(() => {
    if (selectedNorad == null) return null
    const company = registryById[String(selectedNorad)]?.company_key
    return (company && data?.feeds?.by_company[company]) || null
  }, [selectedNorad, registryById, data])

  // Фильтруемый GeoJSON: точки позиций + обогащённые company_key/legal_entity.
  const filterableFC = useMemo(
    () => positionsToFilterableGeoJSON(positions?.satellites ?? [], registryById),
    [positions, registryById],
  )

  // Применить выражение фильтра к слою маркеров (дёшево, без setData). Читает
  // filterRef.current — годится и для применения после смены данных, и по кнопкам
  // панели. Через шлюз: применение до разбора стиля откладывается, а не роняется.
  // null-выражение (фильтр снят) очищает setFilter → показываются все спутники.
  const applyFilter = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      if (!map.getLayer(SAT_LAYER_ID)) return
      map.setFilter(
        SAT_LAYER_ID,
        buildFilterExpression(filterRef.current) as unknown as maplibregl.FilterSpecification | null,
      )
    })
  }, [])

  useEffect(() => {
    if (!containerRef.current) return
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle(),
      // Подпись рисуем сами: встроенный блок MapLibre пишет своё имя в тот же
      // угол и лезет под знак поставщика — см. attributionControl.test.ts
      attributionControl: false,
      center: initialViewRef.current.center,
      zoom: initialViewRef.current.zoom,
      // Рамка домена: карта не уводится туда, где сервер откажет.
      maxBounds: domainMaxBounds(),
    })
    mapRef.current = map
    // Пересоздание карты (StrictMode/ремоунт) — новый шлюз: старая очередь
    // относилась к уже уничтоженной карте (как в MapCanvas).
    const gate = createStyleGate()
    gateRef.current = gate
    // Поворот отключён (как в MapCanvas): компаса нет, координатная сетка прямая.
    map.dragRotate.disable()
    map.touchZoomRotate.disableRotation()
    map.keyboard.disableRotation()
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right')
    map.on('moveend', () => {
      const c = map.getCenter()
      moveEndRef.current({ lng: c.lng, lat: c.lat, zoom: map.getZoom() })
      setBox(boundsToBox(map))
    })
    map.on('load', () => {
      setBox(boundsToBox(map))
      ensureSatIcons(map)
      if (!map.getSource(SAT_SOURCE_ID)) {
        map.addSource(SAT_SOURCE_ID, { type: 'geojson', data: geojsonRef.current })
      }
      if (!map.getLayer(SAT_LAYER_ID)) {
        map.addLayer({
          id: SAT_LAYER_ID,
          type: 'symbol',
          source: SAT_SOURCE_ID,
          layout: {
            // Своя пиктограмма по категории (образы зарегистрированы выше).
            'icon-image': satIconExpr() as unknown as maplibregl.DataDrivenPropertyValueSpecification<string>,
            // Спутники густо стоят у полюсов — без overlap MapLibre выбросил бы часть.
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'icon-size': 0.7,
          },
        })
      }
      // Кольца охвата — В САМОМ НИЗУ (добавлены до слоёв орбиты с тем же beforeId =
      // SAT_LAYER_ID, значит легли под них): линия орбиты и маркеры остаются поверх.
      // Один источник, два слоя (footprint сплошной / dead-zone пунктир — dasharray
      // в MapLibre не data-driven). Данные пустые до включения тумблера/выбора.
      if (!map.getSource(COVERAGE_SOURCE_ID)) {
        map.addSource(COVERAGE_SOURCE_ID, { type: 'geojson', data: EMPTY_FC })
      }
      if (!map.getLayer(COVERAGE_LINE_ID)) {
        map.addLayer(
          {
            id: COVERAGE_LINE_ID,
            type: 'line',
            source: COVERAGE_SOURCE_ID,
            filter: ['==', ['get', 'ring'], 'footprint'] as unknown as maplibregl.FilterSpecification,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': CATEGORY_META.unknown.color,
              'line-width': 1.4,
              'line-opacity': 0.85,
            },
          },
          SAT_LAYER_ID,
        )
      }
      if (!map.getLayer(COVERAGE_DEAD_ID)) {
        map.addLayer(
          {
            id: COVERAGE_DEAD_ID,
            type: 'line',
            source: COVERAGE_SOURCE_ID,
            filter: ['==', ['get', 'ring'], 'deadzone'] as unknown as maplibregl.FilterSpecification,
            layout: { 'line-join': 'round' },
            paint: {
              'line-color': CATEGORY_META.unknown.color,
              'line-width': 1.2,
              'line-opacity': 0.7,
              'line-dasharray': [2, 2],
            },
          },
          SAT_LAYER_ID,
        )
      }
      // Слой орбиты — ПОД маркерами (beforeId), чтобы линия/точка не перекрывали
      // кликабельные пиктограммы. Данные пустые до выбора; цвет проставим по клику.
      if (!map.getSource(ORBIT_SOURCE_ID)) {
        map.addSource(ORBIT_SOURCE_ID, { type: 'geojson', data: EMPTY_FC })
      }
      if (!map.getLayer(ORBIT_LAYER_ID)) {
        map.addLayer(
          {
            id: ORBIT_LAYER_ID,
            type: 'line',
            source: ORBIT_SOURCE_ID,
            layout: { 'line-cap': 'round', 'line-join': 'round' },
            paint: {
              'line-color': CATEGORY_META.unknown.color,
              'line-width': 1.6,
              'line-opacity': 0.9,
            },
          },
          SAT_LAYER_ID,
        )
      }
      if (!map.getSource(ORBIT_DOT_SOURCE_ID)) {
        map.addSource(ORBIT_DOT_SOURCE_ID, { type: 'geojson', data: EMPTY_FC })
      }
      if (!map.getLayer(ORBIT_DOT_LAYER_ID)) {
        map.addLayer(
          {
            id: ORBIT_DOT_LAYER_ID,
            type: 'circle',
            source: ORBIT_DOT_SOURCE_ID,
            paint: {
              'circle-radius': 4,
              'circle-color': CATEGORY_META.unknown.color,
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#ffffff',
            },
          },
          SAT_LAYER_ID,
        )
      }
      // Курсор-указатель над маркером + клик → выбор спутника (орбита + карточка).
      map.on('mouseenter', SAT_LAYER_ID, () => {
        map.getCanvas().style.cursor = 'pointer'
      })
      map.on('mouseleave', SAT_LAYER_ID, () => {
        map.getCanvas().style.cursor = ''
      })
      map.on('click', SAT_LAYER_ID, e => {
        const f = e.features && e.features[0]
        const nid = f?.properties?.norad_id
        if (nid == null) return
        // setState-функция стабильна — безопасно звать из обработчика, созданного
        // один раз. Повторный клик по другому спутнику просто меняет выбор.
        setSelectedNorad(String(nid))
      })
      // Стиль разобран и источники/слои созданы — открываем шлюз: отложенные
      // записи позиций/орбиты, пришедшие до 'load', проигрываются здесь.
      gate.markReady()
    })
    // Смена языка кабинета меняет АДРЕС тайла подложки (у Яндекса `lang=`),
    // а карта строится один раз — учёт довозит новый адрес до неё.
    const unregisterBasemap = registerBasemapMap(map)
    return () => {
      unregisterBasemap()
      map.remove()
      mapRef.current = null
    }
    // Карта создаётся один раз: последующие изменения вида уходят в URL, а не
    // пересоздают карту (иначе пан/зум дёргали бы её на каждом кадре).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Свежие позиции (или обогащение реестром) → обновляем источник и заново
  // применяем текущий фильтр (setData фильтр слоя не сбрасывает, но на пересозданном
  // слое его надо проставить). Через шлюз: приход до разбора стиля откладывается и
  // проигрывается на 'load', а не роняется (см. gateRef).
  useEffect(() => {
    geojsonRef.current = filterableFC
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      const src = map.getSource(SAT_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
      if (src) src.setData(filterableFC)
    })
    applyFilter()
  }, [filterableFC, applyFilter])

  // Смена фильтра → переприменяем выражение к слою (дёшево, без setData).
  useEffect(() => {
    applyFilter()
  }, [filter, applyFilter])

  // Орбита выбранного спутника → линия витка + маркер текущей позиции. Нет выбора
  // (orbit === undefined) → чистим оба источника, чтобы линия не висела на карте.
  // Весь апдейт (включая чистку при снятии выбора) идёт через шлюз: иначе снятие,
  // пришедшее пока грузятся тайлы, терялось бы и линия орбиты застревала на карте.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      const lineSrc = map.getSource(ORBIT_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
      const dotSrc = map.getSource(ORBIT_DOT_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
      if (!lineSrc || !dotSrc) return

      if (!orbit) {
        lineSrc.setData(EMPTY_FC)
        dotSrc.setData(EMPTY_FC)
        return
      }

      const color = categoryColor(orbitCategory(orbit.card))
      lineSrc.setData(orbitTrackToGeoJSON(orbit.track.path))
      if (map.getLayer(ORBIT_LAYER_ID)) map.setPaintProperty(ORBIT_LAYER_ID, 'line-color', color)

      const cur = orbit.track.current
      const dotFc: GeoJSON.FeatureCollection =
        Number.isFinite(cur.lat) && Number.isFinite(cur.lon)
          ? {
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  geometry: { type: 'Point', coordinates: [cur.lon, cur.lat] },
                  properties: {},
                },
              ],
            }
          : EMPTY_FC
      dotSrc.setData(dotFc)
      if (map.getLayer(ORBIT_DOT_LAYER_ID)) map.setPaintProperty(ORBIT_DOT_LAYER_ID, 'circle-color', color)
    })
  }, [orbit])

  // Кольца охвата: тумблер ВКЛ и есть выбор → footprint + мёртвая зона вокруг
  // текущей подспутниковой точки, окрашенные в цвет категории. ВЫКЛ или нет выбора
  // → пустая коллекция (та же дисциплина, что у орбиты: не оставлять кольца висеть).
  // Через шлюз: смена, пришедшая пока грузятся тайлы, не роняется, а проигрывается.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    gateRef.current.run(() => {
      const src = map.getSource(COVERAGE_SOURCE_ID) as maplibregl.GeoJSONSource | undefined
      if (!src) return
      if (showCoverage && orbit) {
        src.setData(coverageFC(orbit.track.current))
        const color = categoryColor(orbitCategory(orbit.card))
        if (map.getLayer(COVERAGE_LINE_ID)) map.setPaintProperty(COVERAGE_LINE_ID, 'line-color', color)
        if (map.getLayer(COVERAGE_DEAD_ID)) map.setPaintProperty(COVERAGE_DEAD_ID, 'line-color', color)
      } else {
        src.setData(EMPTY_FC)
      }
    })
  }, [orbit, showCoverage])

  // Счётчики по категориям для легенды — из позиций (что реально стоит на карте).
  const legendItems = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const s of positions?.satellites ?? []) {
      const cat = s.category || 'unknown'
      counts[cat] = (counts[cat] ?? 0) + 1
    }
    return categoryLegendItems(counts)
  }, [positions])

  // Сколько маркеров проходит фильтр (для шапки панели); total = число фичей на карте.
  const visible = useMemo(
    () => visibleCount(positions?.satellites ?? [], registryById, filter),
    [positions, registryById, filter],
  )

  return (
    <div className="relative h-full w-full">
      {/* Явная высота h-full, НЕ absolute inset-0: MapLibre вешает на контейнер
          класс .maplibregl-map { position: relative }, который перебивает
          tailwind-класс `absolute`, из-за чего inset-0 перестаёт задавать размер
          и контейнер схлопывается по высоте до 0 (карта не видна). h-full от
          высоту не зависит от позиционирования — как в MapCanvas. */}
      <div ref={containerRef} className="h-full w-full" />
      {/* Подпись подложки — требование условий поставщика: у Яндекса знак
          обязан стоять в углу карты и вести на Яндекс Карты, у Carto
          обязана быть видна атрибуция OSM и CARTO. */}
      <BasemapAttribution corner="br" />

      {/* Управление временем — верх по центру, над картой. Под ним, при выбранном
          спутнике, — тумблер колец охвата (footprint + мёртвая зона). */}
      <div className="absolute left-1/2 top-3 z-10 flex -translate-x-1/2 flex-col items-center gap-2">
        <TimeToolbar
          selectedTime={selectedTime}
          onNow={() => setSelectedTime(null)}
          onStep={step => setSelectedTime(base => stepTime(base ?? new Date(), step))}
          onPick={d => setSelectedTime(d)}
        />
        {selectedNorad && orbit && (
          <button
            type="button"
            onClick={() => setShowCoverage(v => !v)}
            aria-pressed={showCoverage}
            className={[
              'pointer-events-auto rounded-lg px-2.5 py-1 text-xs font-semibold shadow-console backdrop-blur transition-colors',
              showCoverage
                ? 'bg-sonar/20 text-foam ring-1 ring-sonar'
                : 'border border-hull bg-abyss/95 text-mist hover:border-sonar hover:text-foam',
            ].join(' ')}
          >
            {t('satellites.coverageRings')}
          </button>
        )}
      </div>

      {/* Панель фасетных фильтров — верхний правый угол, под контролом зума. */}
      {data && (
        <div className="absolute right-3 top-20 z-10">
          <FilterPanel
            facets={data.facets}
            filter={filter}
            onToggle={(dim, value) => setFilter(s => toggleFilterValue(s, dim, value))}
            onReset={() => setFilter(EMPTY_FILTER)}
            visible={visible}
            total={filterableFC.features.length}
          />
        </div>
      )}

      {/* Таймлайн окон пролёта над районом — слева от карточки (чтобы не перекрывать). */}
      {selectedNorad && (
        <PassesPanel norad={selectedNorad} apiTime={apiTime} liveRefetch={liveRefetch} />
      )}

      {/* Карточка выбранного спутника — низ справа (орбита + паспорт). */}
      {selectedNorad && orbit && (
        <SatelliteCard
          data={orbit}
          feed={selectedFeed}
          onClose={() => setSelectedNorad(null)}
        />
      )}

      {/* Карточка-сводка реестра — верхний левый угол, поверх карты (Румб, тема). */}
      <MapPanel storageKey="satellites.panel" className="w-64" title={t('nav.satellites')}>
      <div className="px-3 pb-3">

        {isLoading && <div className="text-xs text-mist">{t('satellites.loadingRegistry')}</div>}

        {isError && (
          <div className="mt-2 text-xs text-mist">{t('satellites.registryFailed')}</div>
        )}

        {data && (
          <>
            <div className="mt-1 text-xs text-mist">
              {t('satellites.inRegistry')}{' '}
              <span className="font-semibold text-foam">{data.count}</span>
            </div>
            <div className="mt-1 text-xs text-mist">
              {t('satellites.inOrbit')}{' '}
              <span className="font-semibold text-foam">{positions?.count ?? '—'}</span>
            </div>
            {data.facets.category.length > 0 && (
              <ul className="mt-3 space-y-1.5 border-t border-hull-hair pt-3">
                {data.facets.category.map(cat => (
                  <li key={cat.value} className="flex items-center justify-between gap-3 text-xs">
                    <span className="truncate text-mist">{categoryLabel(cat.value)}</span>
                    <span className="shrink-0 font-semibold text-foam">{cat.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <div className="mt-3 border-t border-hull-hair pt-3 text-xs">
          <BoundaryToggles
            on={boundaries}
            onToggle={key => setBoundaries(s => ({ ...s, [key]: !s[key] }))}
            meta={boundaryMeta}
          />
        </div>
      </div>
      </MapPanel>

      {/* Легенда категорий — нижний левый угол (закладка ↔ всплывающее тело). */}
      {legendItems.length > 0 && (
        <div className="absolute bottom-4 left-4 z-10">
          <CollapsibleLegend title={t('satellites.categoriesTab')}>
            <div className="min-w-[200px] rounded-xl border border-hull bg-abyss/85 px-3.5 py-3 shadow-console backdrop-blur">
              <div className="mb-2 text-xs font-semibold text-foam">
                {t('satellites.categoriesTitle')}
              </div>
              <ul className="space-y-1.5">
                {legendItems.map(item => (
                  <li
                    key={item.category}
                    className="flex items-center justify-between gap-3 text-[11px] text-mist"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ background: item.color }}
                        aria-hidden
                      />
                      <span className="truncate">{item.label}</span>
                    </span>
                    <span className="shrink-0 font-semibold text-foam">{item.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          </CollapsibleLegend>
        </div>
      )}
    </div>
  )
}
